/**
 * Phaser scene ตัวเดียวที่ใช้ได้ทั้ง 6 ฉาก — ต่างกันแค่ `?scene=`
 * (CLAUDE.md กฎเหล็กข้อ 4: ห้ามเขียน renderer ซ้ำ จอ present ต้องเห็นของเดียวกัน)
 *
 * ฟิสิกส์อยู่ที่นี่ (NB) มือถือส่งแค่ปุ่ม — ทำให้ฟิสิกส์กับการวาดอยู่เครื่องเดียวกัน
 * จึงวาด 60 fps ได้ตรงๆ โดยไม่ต้องเขียน interpolation เลย (PHYSICS.md §0)
 */
import Phaser from "phaser";

import {
  type ActMsg, AVATAR, AVATAR_H_BY_SCENE, COLLIDER_BY_SCENE, FRAMES_PER_SHEET,
  GROUND_Y, isPixelScene, MODE_BY_SCENE, PHYS, PLAYER_TIMEOUT_MS, POS_HZ,
  type PosMsg, type ProfileMsg, SCENE_H, SCENE_W, type SceneId, STYLE_BY_SCENE, TILE_H, TILE_W,
  type StyleId, type SyncMsg,
} from "@py/protocol";

import { facingFrom, newWalkCycle, pickAnim, resolveAnim, startAction, stepWalkCycle, type WalkCycle } from "../anim.js";
import {
  type Buttons, laneSnapVelocity, NO_INPUT, newFeelState, newHopState, stepFeel,
  stepHop, topdownVelocity, type HopState,
} from "../gamefeel.js";
import { findEntryY, type SolidGrid } from "../spawnY.js";
import { mountAmbient } from "./ambient.js";
import { parseTmj, type SceneMap } from "../tmj.js";

export type SpawnRequest = {
  pid: string;
  x: number;
  y: number;
  style: StyleId;
  auth: boolean;
  warp: boolean;
};

/** ทางที่ scene คุยกลับออกไป — engine ไม่รู้จัก MQTT เอง */
export type SceneBridge = {
  publishPos: (msg: PosMsg) => void;
  publishEdge: (pid: string, side: "left" | "right", x: number, y: number) => void;
  publishAct: (msg: Omit<ActMsg, "t">) => void;
  onFps?: (fps: number, players: number) => void;
};

export type SceneOptions = {
  scene: SceneId;
  present: boolean;
  assetBase: string;
  bridge: SceneBridge;
  debug: boolean;
};

type Player = {
  pid: string;
  /** ภาพที่เห็น — ไม่มี physics body ผูกอยู่ ตำแหน่งถูกคัดลอกมาจาก `phys` ทุกเฟรม */
  sprite: Phaser.GameObjects.Sprite;
  /**
   * ตัวชนที่มองไม่เห็น ขนาดเท่า COLLIDER_BY_SCENE เป๊ะ
   *
   * ทำไมต้องแยกจาก sprite: Arcade คูณขนาด body ด้วย scale ของ game object
   * (`Body.setSize` เก็บ sourceWidth แล้วคิด `width = sourceWidth * scaleX`)
   * แต่ภาพตัวละครถูกย่อ/ขยายด้วย `setDisplaySize` ตาม AVATAR_H_BY_SCENE
   * ถ้าผูก body ไว้กับ sprite เดียวกัน ขนาดและ offset จะเพี้ยนตาม scale
   * — เคยทำแบบนั้นแล้วตัวละครเด้งทะลุพื้น (y รายงานกลับ 1421↔1489 แทน 1560)
   * ตัวชนนี้ scale = 1 เสมอ ขนาดจึงตรงตามที่สั่งแน่นอน
   */
  phys: Phaser.Physics.Arcade.Image | null;
  body: Phaser.Physics.Arcade.Body | null;
  auth: boolean;
  style: StyleId;
  input: Buttons;
  lastSeq: number;
  lastInputAt: number;
  feel: ReturnType<typeof newFeelState>;
  hop: HopState;
  /** ระยะยกของ hop ในโหมด topdown (เชิงภาพล้วน — ตัวชนไม่ขยับ) */
  hopY: number;
  /** ตัวนับต่อย/เตะล่าสุดที่เห็น — ใช้ตรวจ edge แบบเดียวกับ jp */
  lastPp: number;
  lastKp: number;
  walk: WalkCycle;
  facing: number;
  blend: number;
  /** ค้างไว้จนกว่าจะโหลด sprite เสร็จ — ห้ามวาดตัวละครกระพริบ */
  ready: boolean;
  /** ระยะยกขึ้นจากพื้นระหว่างอนิเมชันวาร์ป (tween ค่านี้ ไม่ใช่ sprite.y) */
  warpY: number;
  /** ความโปร่งใสระหว่าง tween วาร์ป */
  alpha: number;
};

const SCENE_IMAGE: Partial<Record<SceneId, string>> = {
  1: "scenes/tier2_sky_1080x1920.png",
  2: "scenes/tier3_maze_1080x1920.png",
  3: "scenes/tier4_city_1080x1920.png",
  4: "scenes/tier5_forest_1080x1920.png",
  5: "scenes/tier6_anime_1080x1920.png",
};

export class PlayerScene extends Phaser.Scene {
  private readonly opts: SceneOptions;
  private map!: SceneMap;
  private solidLayer: Phaser.Tilemaps.TilemapLayer | null = null;
  private oneWayGroup!: Phaser.Physics.Arcade.StaticGroup;
  private pickups!: Phaser.Physics.Arcade.StaticGroup;
  private readonly players = new Map<string, Player>();
  private readonly profiles = new Map<string, ProfileMsg>();
  private posAccum = 0;
  private hud: Phaser.GameObjects.Text | null = null;

  constructor(opts: SceneOptions) {
    super("player");
    this.opts = opts;
  }

  preload(): void {
    const { assetBase, scene } = this.opts;
    this.load.tilemapTiledJSON("map", `${assetBase}/tiled/scene${scene}.tmj`);
    this.load.image("collision_tiles", `${assetBase}/tiled/collision_tiles.png`);
    const img = SCENE_IMAGE[scene];
    if (img) this.load.image("bg", `${assetBase}/${img}`);
  }

  create(): void {
    const { scene: sceneId, present, debug } = this.opts;

    const raw = this.cache.tilemap.get("map")?.data;
    this.map = parseTmj(raw);

    if (this.textures.exists("bg")) {
      this.add.image(0, 0, "bg").setOrigin(0, 0).setDisplaySize(SCENE_W, SCENE_H).setDepth(0);
    }
    // ฉาก 0 ไม่มีไฟล์ภาพ — UI เป็น DOM ที่อยู่ข้างหลัง canvas
    // ห้ามตั้งสีพื้นกล้อง ไม่งั้นจะบัง UI ทั้งหมด (ดู transparent ใน phaser/index.ts)

    const tilemap = this.make.tilemap({ key: "map" });
    const ts = tilemap.addTilesetImage("collision", "collision_tiles");
    if (ts && this.map.solid) {
      const layer = tilemap.createLayer("collision", ts, 0, 0);
      if (layer) {
        // ภาพฉากเป็น PNG ทับอยู่แล้ว ชั้นนี้ใช้ชนอย่างเดียว
        layer.setVisible(debug);
        layer.setCollisionByProperty({ solid: true });
        layer.setDepth(1);
        this.solidLayer = layer;
      }
    }

    // one-way จาก object layer — ชนเฉพาะด้านบน เพื่อให้ลอดขึ้นกิ่งได้
    this.oneWayGroup = this.physics.add.staticGroup();
    for (const r of this.map.oneWay) {
      const plat = this.oneWayGroup.create(
        r.x + r.width / 2, r.y + r.height / 2, undefined as unknown as string,
      ) as Phaser.Physics.Arcade.Sprite;
      plat.setVisible(debug).setDisplaySize(r.width, r.height);
      const body = plat.body as Phaser.Physics.Arcade.StaticBody;
      body.setSize(r.width, r.height);
      body.updateFromGameObject();
      body.checkCollision.down = false;
      body.checkCollision.left = false;
      body.checkCollision.right = false;
    }

    this.pickups = this.physics.add.staticGroup();
    this.createPickups();

    // ambient animation ของฉาก (เมฆ/นีออน/กลีบซากุระ ฯลฯ) — เจ้าของงานสั่งข้อ 4
    mountAmbient(this, sceneId, present);

    if (MODE_BY_SCENE[sceneId] === "topdown") this.physics.world.gravity.y = 0;

    if (debug && !present) {
      this.hud = this.add.text(16, 16, "", {
        fontFamily: "monospace", fontSize: "28px", color: "#7cf",
      }).setDepth(100);
    }
  }

  /** ของเก็บมาจาก object layer ใน Tiled — เพิ่มเหรียญได้โดยไม่ต้องแตะโค้ด */
  private createPickups(): void {
    const raw = this.cache.tilemap.get("map")?.data as { layers?: unknown[] } | undefined;
    const layers = (raw?.layers ?? []) as {
      type: string;
      objects?: { type: string; x: number; y: number; width: number; height: number }[];
    }[];
    for (const layer of layers) {
      if (layer.type !== "objectgroup") continue;
      for (const o of layer.objects ?? []) {
        if (o.type !== "coin" && o.type !== "fruit" && o.type !== "pellet") continue;
        const g = this.pickups.create(o.x, o.y, undefined as unknown as string) as
          Phaser.Physics.Arcade.Sprite;
        g.setVisible(this.opts.debug).setDisplaySize(o.width || 40, o.height || 40);
        g.setData("kind", o.type);
        const body = g.body as Phaser.Physics.Arcade.StaticBody;
        body.setSize(o.width || 40, o.height || 40);
        body.updateFromGameObject();
      }
    }
  }

  // ---------------- API ที่ apps/screen เรียก ----------------

  setProfile(pid: string, profile: ProfileMsg | null): void {
    if (profile === null) {
      this.profiles.delete(pid);
      return;
    }
    this.profiles.set(pid, profile);
    const p = this.players.get(pid);
    if (p) void this.loadSprite(p);
  }

  spawnPlayer(req: SpawnRequest): void {
    this.removePlayer(req.pid);

    const sceneId = this.opts.scene;
    const sprite = this.add.sprite(req.x, req.y, "__DEFAULT").setDepth(10);
    sprite.setOrigin(0.5, 1); // เท้าอยู่ที่พิกัด y ที่ได้รับ

    const p: Player = {
      pid: req.pid,
      sprite,
      phys: null,
      body: null,
      auth: req.auth,
      style: req.style || STYLE_BY_SCENE[sceneId],
      input: { ...NO_INPUT },
      lastSeq: -1,
      lastInputAt: performance.now(),
      feel: newFeelState(),
      hop: newHopState(),
      hopY: 0,
      lastPp: -1,
      lastKp: -1,
      walk: newWalkCycle(),
      facing: 1,
      blend: 1,
      ready: false,
      warpY: 0,
      alpha: 1,
    };

    if (req.auth) {
      const [cw, ch] = COLLIDER_BY_SCENE[sceneId];
      // วางจุดกลางของตัวชนให้ก้นตรงกับเท้าที่ y ที่ได้รับมา
      const safeY = this.freeSpotY(req.x, req.y, cw, ch);
      const phys = this.physics.add.image(req.x, safeY - ch / 2, "__DEFAULT");
      phys.setVisible(this.opts.debug).setActive(true);
      const body = phys.body as Phaser.Physics.Arcade.Body;
      // texture 1×1 + scale 1 → sourceWidth ที่สั่งกลายเป็นขนาดจริงตรงๆ
      body.setSize(cw, ch, true);
      body.setMaxVelocity(PHYS.RUN, PHYS.MAX_FALL);
      body.setDragX(MODE_BY_SCENE[sceneId] === "topdown" ? 0 : PHYS.DRAG);
      body.useDamping = false;
      body.setCollideWorldBounds(false);
      p.phys = phys;
      p.body = body;

      if (this.solidLayer) this.physics.add.collider(phys, this.solidLayer);
      this.physics.add.collider(phys, this.oneWayGroup);
      this.physics.add.overlap(phys, this.pickups, (_s, obj) => {
        const g = obj as Phaser.Physics.Arcade.Sprite;
        const kind = (g.getData("kind") as ActMsg["k"]) ?? "coin";
        this.opts.bridge.publishAct({
          p: p.pid, s: sceneId, k: kind, v: 1, x: g.x, y: g.y,
        });
        g.destroy();
      });
    } else {
      // ghost — ห้ามรันฟิสิกส์ วาดตาม sync ล้วน (MQTT_PROTOCOL.md §3 ข้อ 6)
      p.sprite.setAlpha(0);
    }

    this.players.set(req.pid, p);
    void this.loadSprite(p, req.warp);
  }

  /**
   * โหลด sheet ให้เสร็จก่อนโชว์ ไม่งั้นตัวละครหายไป 1-2 วิ (ARCHITECTURE.md §5.5)
   *
   * ทุกทางออกของฟังก์ชันนี้ต้องทำให้ตัวละคร **มองเห็นได้** — ถ้าโหลด sprite ไม่ได้
   * ต้องเป็นเงาดำ ไม่ใช่หายไปเงียบๆ (เคยพลาดตรงนี้: ตกไป texture `__DEFAULT`
   * ซึ่งเป็นภาพ 1×1 โปร่งใส ทำให้ผู้เล่นไม่เห็นอะไรเลยทั้งที่ฟิสิกส์ทำงานอยู่)
   */
  private async loadSprite(p: Player, warp = false): Promise<void> {
    try {
      const profile = this.profiles.get(p.pid);
      const want = profile?.styles[String(p.style) as "1" | "2" | "3" | "4"];
      /*
       * key ต้องพ่วงเวอร์ชันจาก URL (?v=) — sprite ถูกเขียนทับใน R2 ได้
       * (AI วาดเสร็จทีหลัง / ทีมงาน regen ให้) จอที่เปิดค้างต้องเห็นของใหม่
       * โดยไม่ต้อง reload · key เดิมล้วนทำให้แคช texture เก่าค้างตลอดชีพของแท็บ
       */
      const ver = want?.url.split("?v=")[1] ?? "0";
      const key = `sp_${p.pid}_${p.style}_${ver}`;

      if (profile && !this.textures.exists(key)) {
        // สไตล์ 4 (anime) เพิ่งเพิ่มเข้าระบบ — session เก่าไม่มีไฟล์ s4 ใน R2
        // (URL อาจมีแต่โหลดแล้ว 404) ถอยไปใช้สไตล์ 3 ดีกว่าเห็นเงาดำทั้งฉาก
        let img: HTMLImageElement;
        let [fw, fh] = AVATAR[p.style];
        try {
          if (!want) throw new Error(`โปรไฟล์ไม่มีสไตล์ ${p.style}`);
          img = await this.fetchImage(want.url);
        } catch (e) {
          if (p.style !== 4) throw e;
          console.warn(`[scene] s4 ของ ${p.pid} ใช้ไม่ได้ — ใช้สไตล์ 3 แทน`, e);
          img = await this.fetchImage(profile.styles["3"].url);
          [fw, fh] = AVATAR[3];
        }
        // เช็คอีกครั้ง เผื่อมี call อื่นโหลดเสร็จไปก่อนระหว่างรอ
        if (!this.textures.exists(key)) {
          this.textures.addSpriteSheet(key, img, { frameWidth: fw, frameHeight: fh });
        }
      }

      p.sprite.setTexture(profile && this.textures.exists(key) ? key : this.shadowKey(p.style), 0);
    } catch (e) {
      console.warn(`[scene] โหลด sprite ของ ${p.pid} ไม่ได้ ใช้เงาดำแทน`, e);
      p.sprite.setTexture(this.shadowKey(p.style), 0);
    } finally {
      this.fitSprite(p);
      p.ready = true;
      p.sprite.setAlpha(p.auth ? 1 : p.blend);
      if (warp) this.playWarpIn(p);
    }
  }

  /**
   * โหลดรูปแบบ CORS-clean — WebGL อัปโหลด texture จากรูปข้ามโดเมนที่ไม่มี
   * `Access-Control-Allow-Origin` ไม่ได้ (โยน SecurityError)
   *
   * ดึงผ่าน fetch แล้วแปลงเป็น blob: URL ซึ่งนับเป็น same-origin จึงไม่ทำให้ texture
   * ปนเปื้อน · ถ้าปลายทางไม่ยอมให้ fetch (ไม่มี CORS) จะ throw ตรงนี้ให้เห็นชัด
   * ไม่ใช่ล้มเงียบๆ ตอนอัปโหลด texture
   */
  private async fetchImage(url: string): Promise<HTMLImageElement> {
    /*
     * "no-cache" = ใช้แคชได้แต่ต้อง revalidate กับเซิร์ฟเวอร์ก่อนทุกครั้ง
     * (ไฟล์ไม่เปลี่ยน → 304 เร็วเกือบเท่าแคชล้วน · เปลี่ยน → ได้ของใหม่ชัวร์)
     *
     * เดิมใช้ "force-cache" แล้วโดนของจริง: sprite ถูกเขียนทับใน R2 ที่ path เดิม
     * แท็บที่แคชไบต์เก่าไว้ไม่ถามเซิร์ฟเวอร์อีกเลย — จอรวมกับจอเดี่ยวเลยเห็น
     * ตัวละครคนละชุด และชีตเก่าสัดส่วนไม่ตรงถูกตัดเฟรมเพี้ยนเป็นตัวติดกันสองตัว
     */
    const res = await fetch(url, { mode: "cors", cache: "no-cache" });
    if (!res.ok) throw new Error(`HTTP ${res.status} ที่ ${url}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const img = new Image();
      img.src = objectUrl;
      await img.decode();
      return img;
    } finally {
      // ปล่อยทีหลังเล็กน้อย — Phaser อ่าน source image ตอน addSpriteSheet
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
    }
  }

  /**
   * เงาดำสำหรับผู้เล่นที่ยังไม่มี profile (MQTT_PROTOCOL.md §3 ข้อ 8)
   * สร้างเป็น sprite sheet 5 เฟรมด้วย Graphics แล้ว generateTexture
   * (`addSpriteSheetFromAtlas` ใช้กับ texture ที่สร้างแบบนี้ไม่ได้ — เคยพลาดมาแล้ว)
   */
  private shadowKey(style: StyleId): string {
    const key = `shadow_${style}`;
    if (this.textures.exists(key)) return key;

    const [fw, fh] = AVATAR[style];
    // วาดที่ความละเอียดสูงขึ้นเพื่อให้ขอบไม่หยาบตอนขยายเต็มความสูงบนจอ
    const k = Math.max(1, Math.round(64 / fh));
    const w = fw * k;
    const h = fh * k;

    const g = this.make.graphics({ x: 0, y: 0 }, false);
    for (let i = 0; i < FRAMES_PER_SHEET; i++) {
      const ox = i * w;
      g.fillStyle(0x0d0f16, 1);
      // หัว + ลำตัว: อ่านออกว่าเป็นคน แม้ยังไม่รู้ว่าหน้าตายังไง
      g.fillCircle(ox + w * 0.5, h * 0.17, w * 0.26);
      g.fillRoundedRect(ox + w * 0.26, h * 0.3, w * 0.48, h * 0.68, w * 0.16);
      g.lineStyle(Math.max(1, k), 0x2a3110, 1);
      g.strokeCircle(ox + w * 0.5, h * 0.17, w * 0.26);
    }
    g.generateTexture(`${key}_src`, w * FRAMES_PER_SHEET, h);
    g.destroy();

    const src = this.textures.get(`${key}_src`).getSourceImage() as HTMLImageElement;
    this.textures.addSpriteSheet(key, src, { frameWidth: w, frameHeight: h });
    return key;
  }

  /**
   * ขนาดบนจอกำหนดที่ AVATAR_H_BY_SCENE — ฐาน top-down ต้องเล็กพอเดินผ่าน 1 tile
   *
   * ต้องตั้ง collider **ที่นี่** ไม่ใช่ตอน spawn: ตอน spawn ยังเป็น texture
   * `__DEFAULT` ขนาด 1×1 ถ้าคำนวณ offset จากตอนนั้นจะได้ค่าผิด แล้วตัวละคร
   * จะเด้ง/ร่วงทะลุพื้นทั้งที่ฟิสิกส์ทำงาน (เคยพลาด: y รายงานกลับ 1421↔1489 แทน 1560)
   *
   * สูตร offset ใช้หน่วยพิกเซลบนจอ (displayWidth/Height) เพราะ origin อยู่ที่เท้า:
   *   ซ้ายสุดของ body ต้องอยู่กลางภาพ  → offX = (displayWidth - cw) / 2
   *   ก้นของ body ต้องตรงกับเท้าของภาพ → offY = displayHeight - ch
   */
  private fitSprite(p: Player): void {
    const h = AVATAR_H_BY_SCENE[this.opts.scene];
    const [fw, fh] = AVATAR[p.style];
    p.sprite.setDisplaySize((fw / fh) * h, h);
  }

  /**
   * เลื่อน y ของจุดเกิดลงมาให้ตัวชนไม่จมอยู่ในกำแพง
   *
   * ที่ต้องมีเพราะ `GROUND_Y = 1560` เป็นเส้นพื้นของฉากแนวข้าง แต่ฉาก top-down
   * ใช้ y เป็นความลึกในห้อง ตอนเดินข้ามฉากเราส่ง y เดิมติดไปด้วย ตัวชนสูง 40 px
   * ที่ก้นอยู่ 1560 จึงไปกินแถว y 1500–1559 ซึ่งเป็นแถวโต๊ะของฐาน 1
   * ผลคือข้ามเข้ามาแล้วเดินได้อีกนิดเดียวก็ติดโต๊ะ ขยับต่อไม่ได้เลย
   * (จุดเกิดใน `scene1.tmj` เองที่ x=540 ก็จมโต๊ะด้วยเหตุผลเดียวกัน)
   *
   * แค่ "ไม่จมกำแพง" ยังไม่พอสำหรับฉาก top-down เพราะจุดที่ยืนได้อาจเป็นซอกตัน
   * ตอนข้ามเข้าฐาน 1 ที่ x=90 y=1560 นั้นยืนได้จริง แต่เดินขวาไป 128 px ก็ชนโต๊ะ
   * แล้วไปต่อไม่ได้เลย · จึงต้องเลือก **แถวที่เดินทะลุซ้าย-ขวาได้จริง**
   * ไม่ใช่แค่ช่องว่างช่องเดียว (ฐาน 1 คือ y 1600–1679 · ฐาน 3 คือ y 1598–1619)
   *
   * คำนวณจากผังจริงทุกครั้ง ไม่ฮาร์ดโค้ดตัวเลข เพิ่มฐานใหม่แล้วใช้ได้ทันที
   * ฉากแนวข้างไม่ใช้กติกานี้ เพราะแถวของมันไม่มีทางว่างทั้งแถวอยู่แล้ว
   */
  private freeSpotY(x: number, y: number, cw: number, ch: number): number {
    const layer = this.solidLayer;
    if (!layer) return y;
    const map = layer.tilemap;
    const grid: SolidGrid = {
      cols: map.width, rows: map.height,
      tileW: map.tileWidth, tileH: map.tileHeight,
      solid: (c, r) => layer.getTileAt(c, r)?.collides === true,
    };
    const got = findEntryY(grid, x, y, {
      collider: [cw, ch],
      requireLane: MODE_BY_SCENE[this.opts.scene] === "topdown",
      sceneH: SCENE_H,
    });
    if (got === null) {
      console.error(`[spawn] หาแถวที่เดินได้ให้ x=${x} y=${y} ไม่เจอ — ปล่อยไว้ที่เดิม`);
      return y;
    }
    if (got !== y) {
      console.warn(`[spawn] ย้ายจุดเกิดจาก y=${y} ไป y=${got} (ที่เดิมเดินต่อไม่ได้)`);
    }
    return got;
  }

  /** คัดลอกตำแหน่งจากตัวชนมาที่ภาพ — เท้าของภาพอยู่ที่ก้นของ body */
  private syncSpriteToBody(p: Player): void {
    if (!p.body) return;
    p.sprite.x = p.body.center.x;
    p.sprite.y = p.body.bottom + p.warpY - p.hopY;
  }

  /**
   * ตัวละครประกอบตัวแล้วร่วงลงพื้น (ใช้ที่ฉาก 0 หลังลงทะเบียน)
   *
   * ต้อง tween ค่า `warpY` แล้วให้ syncSpriteToBody เอาไปบวก ไม่ใช่ tween `sprite.y`
   * ตรงๆ เพราะทุกเฟรมภาพถูกเซ็ตตำแหน่งใหม่จากตัวชน จะแย่งกับ tween
   */
  private playWarpIn(p: Player): void {
    p.sprite.setAlpha(0);
    p.warpY = -260;
    this.tweens.add({
      targets: p, alpha: 1, warpY: 0, duration: 620, ease: "Cubic.easeOut",
      onUpdate: () => p.sprite.setAlpha(p.alpha),
    });
  }

  applyInput(pid: string, msg: {
    l: number; r: number; u: number; d: number;
    j: number; jp: number; pp?: number; kp?: number; seq: number;
  }): void {
    const p = this.players.get(pid);
    if (!p) return;
    if (msg.seq < p.lastSeq) return; // ทิ้งแพ็กเก็ตเก่า (มาไม่เรียงได้ที่ QoS 0)
    p.lastSeq = msg.seq;
    p.lastInputAt = performance.now();
    p.input = { l: msg.l, r: msg.r, u: msg.u, d: msg.d, j: msg.j, jp: msg.jp };
    // ต่อย/เตะอ่านจากตัวนับแบบเดียวกับ jp (QoS 0) — เฟรมแรกอย่านับของค้าง
    const pp = msg.pp ?? 0;
    const kp = msg.kp ?? 0;
    if (p.lastPp === -1) p.lastPp = pp;
    if (p.lastKp === -1) p.lastKp = kp;
    if (pp !== p.lastPp) { p.lastPp = pp; startAction(p.walk, "punch"); }
    if (kp !== p.lastKp) { p.lastKp = kp; startAction(p.walk, "kick"); }
  }

  applySync(pid: string, msg: SyncMsg): void {
    const p = this.players.get(pid);
    if (!p || p.auth) return; // จอที่ถือ authority ไม่รับพิกัดจากใคร
    p.lastInputAt = performance.now();
    p.sprite.x = msg.x;
    p.sprite.y = msg.y;
    p.facing = msg.f;
    p.blend = msg.blend;
    p.sprite.setAlpha(p.ready ? msg.blend : 0);
    p.sprite.setFlipX(msg.f < 0);
    if (p.sprite.texture.key !== "__DEFAULT") p.sprite.setFrame(msg.fr);
  }

  removePlayer(pid: string): void {
    const p = this.players.get(pid);
    if (!p) return;
    p.sprite.destroy();
    p.phys?.destroy();
    this.players.delete(pid);
  }

  clearPlayers(): void {
    for (const pid of [...this.players.keys()]) this.removePlayer(pid);
  }

  setDebug(v: boolean): void {
    this.opts.debug = v;
    this.solidLayer?.setVisible(v);
    this.hud?.setVisible(v);
  }

  get playerCount(): number {
    return this.players.size;
  }

  // ---------------- ลูปหลัก ----------------

  override update(_time: number, delta: number): void {
    const sceneId = this.opts.scene;
    const mode = MODE_BY_SCENE[sceneId];
    /**
     * ⚠️ ห้ามใช้ this.time.now วัด timeout — นาฬิกาของ Phaser **หยุดเดิน**
     * ตอนแท็บถูกซ่อน แต่ MQTT ยังส่ง spawn/input เข้ามาตลอด (WebSocket ไม่หยุด)
     * แท็บพื้นหลังจึงประทับ lastInputAt ด้วยเวลาที่ค้าง พอผู้ชมสลับมาดูแท็บนั้น
     * now กระโดดไปข้างหน้าทีเดียวเท่าช่วงที่ซ่อน → ทุกตัวถูกลบก่อนวาดเฟรมแรก
     * อาการที่เห็นคือ "เดินข้ามฐานแล้วตัวละครหายไปเลย" (ไล่กันอยู่นานมาก)
     * performance.now() เดินตลอดไม่สนว่าแท็บหลับหรือตื่น
     */
    const now = performance.now();

    for (const p of [...this.players.values()]) {
      // LWT ไม่พอ เพราะ broker ไม่รู้ว่ามือถืออยู่ฉากไหน — timeout เป็นกลไกหลัก
      if (now - p.lastInputAt > PLAYER_TIMEOUT_MS) {
        this.removePlayer(p.pid);
        continue;
      }
      if (!p.auth || !p.body) continue;

      const body = p.body;

      // ตาข่ายชั้นสุดท้าย: หลุดใต้โลกเมื่อไหร่ (ไม่ว่าด้วยเหตุใด) ดึงกลับมายืนบนพื้น
      // ดีกว่าปล่อยร่วงไม่จบ — ผู้ชมหน้างานไม่มีทางรู้ว่าต้องลบ session เอง
      if (body.top > SCENE_H + 40) {
        console.warn(`[scene] ${p.pid} หลุดใต้โลก (y=${Math.round(body.bottom)}) — ดึงกลับขึ้นพื้น`);
        body.position.y = GROUND_Y - body.height;
        body.setVelocityY(0);
        this.syncSpriteToBody(p);
      }
      const onGround = body.blocked.down || body.touching.down;

      if (mode === "topdown") {
        const v = topdownVelocity(p.input);
        body.setVelocity(v.vx, v.vy);
        // lane assist: กดแกนเดียว → ประคองอีกแกนเข้ากลางช่อง (เลี้ยวมุมลื่น)
        if (v.vx !== 0 && v.vy === 0) {
          body.setVelocityY(laneSnapVelocity(body.center.y, TILE_H));
        } else if (v.vy !== 0 && v.vx === 0) {
          body.setVelocityX(laneSnapVelocity(body.center.x, TILE_W));
        }
        // กระโดดในฉากมองบน = เด้งเชิงภาพ ไม่แตะตัวชน (เจ้าของงานสั่งข้อ 5)
        p.hopY = stepHop(p.hop, p.input.jp, delta);
      } else {
        const feel = stepFeel(p.feel, p.input, delta, onGround, body.velocity.y);
        body.setAccelerationX((p.input.r - p.input.l) * PHYS.ACCEL);
        if (p.input.l === 0 && p.input.r === 0) body.setAccelerationX(0);
        if (feel.jump) body.setVelocityY(PHYS.JUMP_V);
        if (feel.cutJump) body.setVelocityY(body.velocity.y * PHYS.JUMP_CUT);
        // ปิดชนด้านบนชั่วคราว = ลอดลงจากกิ่งได้ตามที่ตั้งใจ
        body.checkCollision.up = !feel.dropThru;
      }

      // hop กลางอากาศให้ท่ากระโดด/ตกตามจังหวะพาราโบลา
      const hopping = mode === "topdown" && p.hopY > 0;
      const baseAnim = hopping
        ? (p.hop.leftMs > 180 ? "jump" as const : "fall" as const)
        : pickAnim(mode, body.velocity.x, body.velocity.y, onGround);
      // ท่าต่อย/เตะชนะท่าจากฟิสิกส์จนกว่าจะเล่นจบ
      const anim = resolveAnim(p.walk, baseAnim, delta);
      const speed = mode === "topdown"
        ? Math.hypot(body.velocity.x, body.velocity.y)
        : body.velocity.x;
      const frame = stepWalkCycle(p.walk, anim, speed, delta);
      p.facing = facingFrom(body.velocity.x, p.facing);
      p.sprite.setFlipX(p.facing < 0);
      if (p.ready && p.sprite.texture.key !== "__DEFAULT") p.sprite.setFrame(frame);
      if (p.ready) p.sprite.setAlpha(1);
      // ภาพเดินตามตัวชน ไม่ใช่กลับกัน
      this.syncSpriteToBody(p);

      /*
       * ตรึงตัวชนไว้ที่เส้นขอบ — **หัวใจของการข้ามฉากที่ไม่ตก**
       *
       * ระหว่างรอมือถือส่งมอบ (edge → มือถือ → spawn/release ≈ 100-250ms
       * หรือนานกว่านั้นตอนเน็ตสะดุด) จอนี้ยังจำลองฟิสิกส์ต่อ ถ้าปล่อยให้เดิน
       * พ้นขอบผัง ตัวละครจะหมดพื้นรองรับแล้ว**ร่วงลงเหวข้างฉาก** —
       * ผู้ชมเห็น "เดินไปรอยต่อแล้วตกหาย" ทุกครั้งที่เครือข่ายไม่เร็วพอ
       * ตรึงไว้ที่ขอบ (ยังเข้าเขตยิง edge ที่ <4/>1076) ให้ยันขอบรอส่งมอบแทน
       */
      if (body.center.x < 2) {
        body.position.x = 2 - body.width / 2;
        if (body.velocity.x < 0) body.setVelocityX(0);
        this.syncSpriteToBody(p);
      } else if (body.center.x > SCENE_W - 2) {
        body.position.x = SCENE_W - 2 - body.width / 2;
        if (body.velocity.x > 0) body.setVelocityX(0);
        this.syncSpriteToBody(p);
      }

      // ถึงขอบแล้วบอกมือถือ — NB ห้ามคุยกับ NB อื่นเอง
      if (p.sprite.x > SCENE_W - 4 || p.sprite.x < 4) {
        this.opts.bridge.publishEdge(
          p.pid, p.sprite.x < 4 ? "left" : "right", p.sprite.x, p.sprite.y,
        );
      }
    }

    // รายงานพิกัดกลับให้มือถือเก็บในฐานะ database
    this.posAccum += delta;
    const period = 1000 / POS_HZ;
    if (this.posAccum >= period) {
      this.posAccum %= period;
      for (const p of this.players.values()) {
        if (!p.auth || !p.body) continue;
        const body = p.body;
        this.opts.bridge.publishPos({
          p: p.pid, s: sceneId,
          x: Math.round(p.sprite.x), y: Math.round(p.sprite.y),
          vx: Math.round(body.velocity.x), vy: Math.round(body.velocity.y),
          g: body.blocked.down ? 1 : 0,
          // ใช้ท่าที่วาดจริง (p.walk.anim) ไม่ใช่คำนวณใหม่ — จะได้เห็น punch/kick ด้วย
          a: (p.walk.anim || "idle") as never,
          f: p.facing,
          t: Date.now(),
        });
      }
    }

    if (this.hud) {
      this.hud.setText(
        `scene ${sceneId} ${this.map.name}\nfps ${Math.round(this.game.loop.actualFps)}\nplayers ${this.players.size}\nground ${GROUND_Y}`,
      );
    }
    this.opts.bridge.onFps?.(Math.round(this.game.loop.actualFps), this.players.size);
  }
}
